import { fileTypeFromBuffer } from 'file-type'
import sanitize from 'sanitize-filename'

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024

const EXTENSION_MIME_MAP: Record<string, string[]> = {
  pdf:  ['application/pdf'],
  docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  txt:  [],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  csv:  [],
  jpg:  ['image/jpeg'],
  jpeg: ['image/jpeg'],
  png:  ['image/png'],
}

export const ALLOWED_FILE_EXTENSIONS = Object.keys(EXTENSION_MIME_MAP)
export const ALLOWED_FILE_ACCEPT = ALLOWED_FILE_EXTENSIONS.map(ext => `.${ext}`).join(',')

/**
 * Detects if a byte array is UTF-16 encoded by checking for BOMs or
 * high frequency of 0x00 bytes at even or odd indices.
 */
function isUtf16Encoding(bytes: Uint8Array): boolean {
  if (bytes.length < 2) return false

  // Check for UTF-16 BOMs
  const bom = (bytes[0] << 8) | bytes[1]
  if (bom === 0xFEFF || bom === 0xFFFE) return true

  // Check for high frequency of 0x00 bytes (UTF-16 characteristic)
  let evenZeros = 0
  let oddZeros = 0
  const sampleSize = Math.min(bytes.length, 100)

  for (let i = 0; i < sampleSize; i++) {
    if (bytes[i] === 0) {
      if (i % 2 === 0) {
        evenZeros++
      } else {
        oddZeros++
      }
    }
  }

  const threshold = sampleSize * 0.3
  return evenZeros > threshold || oddZeros > threshold
}

export function sanitizeFileName(name: string): string {
  const sanitized = sanitize(name, { replacement: '_' }).trim().replace(/\s+/g, '_')
  return sanitized || 'file'
}

export function sanitizeTitle(title: string): string {
  return title.trim().replace(/\s+/g, '_')
}

export async function validateFileContents(file: File): Promise<string | null> {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''

  if (!(extension in EXTENSION_MIME_MAP)) {
    return `Unsupported file type (.${extension || 'unknown'}).`
  }

  const allowedMimes = EXTENSION_MIME_MAP[extension]

  const buffer = await file.slice(0, 4100).arrayBuffer()

  if (allowedMimes.length === 0) {
    const bytes = new Uint8Array(buffer)
    const hasNulByte = bytes.some((b) => b === 0)
    if (hasNulByte) {
      // Check for UTF-16 encoding before rejecting for NUL bytes
      const isUtf16 = isUtf16Encoding(bytes)
      const textExtensions = ['txt', 'csv', 'md', 'json', 'xml']
      if (isUtf16 && textExtensions.includes(extension)) {
        return null
      }
      return `File contents do not match the .${extension} extension.`
    }
    return null
  }

  const detected = await fileTypeFromBuffer(buffer)

  if (!detected) {
    return `Could not verify file contents for .${extension} file.`
  }

  if (!allowedMimes.includes(detected.mime)) {
    return `File contents do not match the .${extension} extension.`
  }

  return null
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

export function validateFileSize(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File is too large (${formatFileSize(file.size)}). Maximum file size is 50MB.`
  }
  return null
}
